/**
 * Tasks from text: Claude reads a note and proposes action items (structured
 * output); the user picks which ones become tasks.
 */
import { dueToken, normalTime, PRIORITY_EMOJI, type TaskPriority } from '@nexus/core';
import type { AiModel, AiUsage, MessageParam } from './protocol';
import { AiError, callOnce, type Transport } from './chat';

export interface ProposedTask {
  title: string;
  due: string | null;
  time: string | null;
  priority: TaskPriority | null;
  quote: string;
}

const PRIORITIES = new Set<string>(Object.keys(PRIORITY_EMOJI));

export function extractMessage(opts: {
  title: string;
  markdown: string;
  today: string;
  weekday: string;
}): MessageParam {
  const title = opts.title.replace(/"/g, "'");
  return {
    role: 'user',
    content: `Today is ${opts.weekday} ${opts.today}.\n\n<page title="${title}">\n${opts.markdown}\n</page>`,
  };
}

/** Validates Claude's JSON; anything malformed is dropped rather than trusted. */
export function parseProposals(json: string): ProposedTask[] {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    throw new AiError('api', 'No valid task list in the answer.');
  }
  const list = (data as { tasks?: unknown })?.tasks;
  if (!Array.isArray(list)) throw new AiError('api', 'No valid task list in the answer.');
  const out: ProposedTask[] = [];
  for (const raw of list as Record<string, unknown>[]) {
    const title = typeof raw?.title === 'string' ? raw.title.replace(/\s+/g, ' ').trim() : '';
    if (!title) continue;
    const due = typeof raw.due === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.due) ? raw.due : null;
    const time =
      due && typeof raw.time === 'string' && /^\d{1,2}:\d{2}$/.test(raw.time)
        ? normalTime(raw.time)
        : null;
    const priority =
      typeof raw.priority === 'string' && PRIORITIES.has(raw.priority)
        ? (raw.priority as TaskPriority)
        : null;
    const quote = typeof raw.quote === 'string' ? raw.quote.trim() : '';
    out.push({ title, due, time, priority, quote });
  }
  return out;
}

/** The checkbox text for a task, in the Tasks notation the app parses. */
export function taskText(t: Pick<ProposedTask, 'title' | 'due' | 'time' | 'priority'>): string {
  const parts = [t.title.trim()];
  if (t.due) parts.push(`📅 ${dueToken(t.due, t.time)}`);
  if (t.priority) parts.push(PRIORITY_EMOJI[t.priority]);
  return parts.join(' ');
}

export async function runExtract(opts: {
  transport: Transport;
  model: AiModel;
  message: MessageParam;
  signal?: AbortSignal;
  onUsage?: (usage: AiUsage) => void;
}): Promise<ProposedTask[]> {
  const done = await callOnce(
    opts.transport,
    { mode: 'extract', model: opts.model, messages: [opts.message] },
    opts.signal,
    { onUsage: opts.onUsage },
  );
  if (done.stop_reason === 'refusal') throw new AiError('refusal');
  const text = done.content
    .filter((b) => b.type === 'text')
    .map((b) => (b as { text: string }).text)
    .join('');
  return parseProposals(text);
}
